import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { getCookie, setCookie } from '@literax/utils/cookie.utils';

import { GuestService } from '@literax/services/guest/guest.service';
import { ILinkValidateResponse } from '@literax/models/document.model';
import { Injectable } from '@angular/core';
import { LinkOutcomeService } from '@literax/services/link-outcome/link-outcome.service';
import jwt_decode from 'jwt-decode';

/**
 * Second guard on the `sign/:token` route, which checks whether a single-use link has already
 * been opened.
 *
 * Like the entry guard it now resolves to `true` or a `UrlTree`, never to `of(error)` — an
 * observable carrying an error object is neither, and Angular treated it as "activate", which let
 * a recipient reach an empty workspace instead of a screen (FR-004).
 *
 * It repeats the entry guard's legibility check rather than relying on it, because Angular does
 * **not** run the two in sequence. `runCanActivate` wraps each guard in `defer()` and hands the
 * list to `prioritizedGuardValue`, which subscribes to all of them at once through
 * `combineLatest`; the priority rule only decides whose answer wins, never whose code runs. So
 * without this check a cut link still issued `GET /guests/visit_document`, which FR-027 forbids.
 */
@Injectable({
  providedIn: 'root',
})
export class ValidateLinkGuard implements CanActivate {
  token: string;
  TokenDecoded: Record<string, string>;

  constructor(
    private guestService: GuestService,
    private router: Router,
    private linkOutcome: LinkOutcomeService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    this.token = route.paramMap.get('token');

    return this.isValid();
  }

  /**
   * Checks the single-use state of the link.
   *
   * @returns `true` when the link may be opened, or a `UrlTree` for the screen that explains why
   * it cannot.
   */
  isValid(): Observable<boolean | UrlTree> | boolean | UrlTree {
    const tokenDecoded = this.decode(this.token);

    // Decided before `validateLink` is called, not inside it: an unreadable token carries nothing
    // to ask the backend about, and FR-027 requires the outcome to be reached with no request at
    // all. Returning synchronously is what makes that visible.
    // `?? true` for the same reason the entry guard's destinationFor has it: `urlTree` is typed
    // `UrlTree | null`, this app compiles with `strict: false`, and a null returned from
    // canActivate is read by Angular as "block, no redirect" — a blank screen, which is the defect
    // this feature exists to remove. It cannot be null today; the guard is there so it stays that
    // way if LINK_OUTCOME_ROUTES ever changes.
    if (tokenDecoded === null) {
      return this.linkOutcome.resolveTruncated().urlTree ?? true;
    }

    this.TokenDecoded = tokenDecoded;

    return this.guestService.validateLink(this.token).pipe(
      map((response: ILinkValidateResponse) => {
        if (response.isSingleUseLinkConfigActive && !response.visited) {
          this.guestService.updateLink(true, this.token).subscribe(() => {
            setCookie(
              `VISIT_DOCUMENT_${this.TokenDecoded['uuid']}_${this.TokenDecoded['request']}`,
              `${this.TokenDecoded['uuid']}_${this.TokenDecoded['request']}`
            );
          });
        }

        if (response.isSingleUseLinkConfigActive && response.visited) {
          return this.checkCookie();
        }

        return true;
      }),
      catchError((error) => {
        const { urlTree } = this.linkOutcome.resolve(error);

        return of(urlTree ?? true);
      })
    );
  }

  /**
   * Reads the token's claims, without deciding anything from them.
   *
   * Mirrors the entry guard's own `decode`. Expiry is never judged here — that verdict belongs to
   * the backend, which knows an observer's link does not expire (FR-026, FR-024).
   *
   * @param token The guest token taken from the route.
   * @returns The decoded claims, or `null` when the token cannot be read at all.
   */
  private decode(token: string): Record<string, string> | null {
    try {
      return jwt_decode(token);
    } catch {
      return null;
    }
  }

  /**
   * Checks whether this browser is the one that opened the single-use link.
   *
   * @returns `true` when the cookie matches, or the expired-link screen when it does not.
   */
  checkCookie(): boolean | UrlTree {
    const cookie = getCookie(
      `VISIT_DOCUMENT_${this.TokenDecoded['uuid']}_${this.TokenDecoded['request']}`
    );
    const splitted = (cookie ?? '').split('_', 2);

    if (
      splitted[0] === this.TokenDecoded['uuid'] &&
      splitted[1] === this.TokenDecoded['request']
    ) {
      return true;
    }

    // createUrlTree, not parseUrl: the token is a JWT and must go through the same matrix
    // parameter the previous navigate() call used, without being re-parsed as a URL.
    return this.router.createUrlTree(['/expired-link', { token: this.token }]);
  }
}
